/**
 * Cloudflare Tunnel — OQ-337.
 *
 * Man hinh nay KHONG chay tunnel ho nguoi dung. No tao/xoa/cau hinh tren
 * Cloudflare roi dua ra lenh de ho tu chay `cloudflared` tren may cua ho.
 *
 * Ba dieu bat buoc phai noi ra, vi khong noi thi nguoi dung tuong da xong:
 *   1. Tunnel moi tao chua phuc vu gi ca (`inactive`) cho toi khi co may chay.
 *   2. Token cai dat chi hien khi bam, va no manh ngang mat khau.
 *   3. Ten mien phai duoc tro bang CNAME toi `<uuid>.cfargotunnel.com`.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, thanhApiError, useLoiApi } from '../lib/api';
import { useT } from '../lib/i18n';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Modal';
import { Field } from '../components/Field';
import { ApiErrorAlert } from '../components/ApiErrorAlert';
import { AsyncSection, EmptyState } from '../components/States';
import { useToast } from '../components/Toast';

export interface TunnelConnectionView {
  id: string;
  coloName: string | null;
  originIp: string | null;
  clientVersion: string | null;
  openedAt: string | null;
}

export interface Tunnel {
  id: string;
  name: string;
  status: string;
  remoteConfig: boolean;
  connectionCount: number;
  createdAt: string | null;
  cnameTarget: string;
  connections: TunnelConnectionView[];
}

export interface TunnelToken {
  token: string;
  installCommand: string;
  dockerCommand: string;
}

export interface IngressRow {
  hostname: string | null;
  service: string;
  path: string | null;
  /** Tuỳ chọn nâng cao đặt trên Cloudflare — chỉ đọc (OQ-341). */
  advanced?: Record<string, unknown> | null;
}

export interface TunnelConfig {
  ingress: IngressRow[];
  editable: boolean;
  advancedAll?: Record<string, unknown> | null;
}

/**
 * Doi mot gia tri cau hinh thanh chuoi doc duoc.
 *
 * `access` la object long nhau (`{ required, teamName, audTag[] }`), con lai
 * phan lon la boolean/so/chuoi. Hien `[object Object]` thi bang nay vo dung —
 * ma day dung la muc quan trong nhat trong 16 muc.
 *
 * NHAN boolean di qua tham so chu khong viet thang: `bật`/`tắt` la chu hien ra
 * man hinh, nen no phai doi theo ngon ngu (OQ-290). Ham thuan de test duoc.
 */
export function hienGiaTri(v: unknown, nhan: { bat: string; tat: string }): string {
  if (v === true) return nhan.bat;
  if (v === false) return nhan.tat;
  if (v === null || v === undefined) return '—';
  if (Array.isArray(v)) return v.map((x) => hienGiaTri(x, nhan)).join(', ');
  if (typeof v === 'object') {
    return Object.entries(v as Record<string, unknown>)
      .map(([k, x]) => `${k}: ${hienGiaTri(x, nhan)}`)
      .join(' · ');
  }
  return String(v);
}

export function demNangCao(o: Record<string, unknown> | null | undefined): number {
  return o ? Object.keys(o).length : 0;
}

/**
 * Mau chip theo trang thai — KHONG dung mau xanh cho `inactive`.
 *
 * `inactive` nghia la chua may nao chay `cloudflared`, tuc tunnel chua phuc vu
 * duoc luu luong nao. To no mau trung tinh hay mau tot deu lam nguoi dung tin
 * rang moi thu on, roi ho di tro DNS vao mot cai cong dong.
 */
export function chipTrangThai(status: string): string {
  if (status === 'healthy') return 'ok';
  if (status === 'degraded') return 'warn';
  return 'danger';
}

export function TunnelsModal({
  connectionId,
  connectionName,
  canWrite,
  onClose,
}: {
  connectionId: string;
  connectionName: string;
  canWrite: boolean;
  onClose: () => void;
}) {
  const t = useT();
  const qc = useQueryClient();
  const toast = useToast();
  const [tenMoi, setTenMoi] = useState('');
  const [dangXem, setDangXem] = useState<Tunnel | null>(null);
  const { error, setError, xoaLoiKhiGo } = useLoiApi();

  const danhSach = useQuery({
    queryKey: ['tunnels', connectionId],
    queryFn: () => api.get<Tunnel[]>(`/connections/${connectionId}/tunnels`),
    retry: false,
  });

  const tao = useMutation({
    mutationFn: () =>
      api.post<Tunnel>(`/connections/${connectionId}/tunnels`, { name: tenMoi.trim() }),
    onSuccess: async () => {
      setTenMoi('');
      setError(null);
      await qc.invalidateQueries({ queryKey: ['tunnels', connectionId] });
      toast.toast(t('tunnel.created'));
    },
    onError: (err: unknown) => setError(thanhApiError(err)),
  });

  const xoa = useMutation({
    mutationFn: (id: string) => api.del<void>(`/connections/${connectionId}/tunnels/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['tunnels', connectionId] });
      toast.toast(t('tunnel.deleted'));
    },
    onError: (err: unknown) => setError(thanhApiError(err)),
  });

  return (
    <Modal
      open
      title={t('tunnel.title')}
      subtitle={t('tunnel.subtitle', { name: connectionName })}
      onClose={onClose}
      onFieldChange={xoaLoiKhiGo.onChange}
      footer={
        <button className="btn" type="button" onClick={onClose}>
          {t('common.close')}
        </button>
      }
    >
      {error ? <ApiErrorAlert error={error} /> : null}

      <div className="scope-note">
        <Icon name="info" size={17} />
        <span>{t('tunnel.intro')}</span>
      </div>

      {canWrite ? (
        <div className="tunnel-them">
          <Field
            label={t('tunnel.newName')}
            placeholder={t('tunnel.newNamePlaceholder')}
            autoComplete="off"
            value={tenMoi}
            onChange={(e) => setTenMoi(e.target.value)}
            error={error?.fieldMessage('name')}
          />
          <button
            className="btn btn-primary"
            type="button"
            data-od-id="tunnel-create"
            disabled={tao.isPending || tenMoi.trim().length === 0}
            onClick={() => tao.mutate()}
          >
            {tao.isPending ? t('common.processing') : t('tunnel.create')}
          </button>
        </div>
      ) : null}

      <AsyncSection
        loading={danhSach.isLoading}
        error={danhSach.error}
        data={danhSach.data}
        onRetry={() => void danhSach.refetch()}
        empty={<EmptyState title={t('tunnel.emptyTitle')} body={t('tunnel.emptyHint')} />}
      >
        {(list) =>
          list.length === 0 ? (
            <EmptyState title={t('tunnel.emptyTitle')} body={t('tunnel.emptyHint')} />
          ) : (
            <div className="table-wrap">
              <table className="tbl" data-od-id="tunnel-table">
                <thead>
                  <tr>
                    <th>{t('tunnel.colName')}</th>
                    <th>{t('tunnel.colStatus')}</th>
                    <th>{t('tunnel.colConns')}</th>
                    <th className="right">{t('tunnel.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((tn) => (
                    <tr key={tn.id} data-od-id={`tunnel-${tn.id}`} data-state={tn.status}>
                      <td>
                        <div className="cell-main tunnel-ten">
                          <b>{tn.name}</b>
                          {tn.remoteConfig ? null : (
                            <span className="chip warn" title={t('tunnel.localHint')}>
                              {t('tunnel.local')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`chip ${chipTrangThai(tn.status)}`}>
                          <span className="dot" />
                          {t(`tunnel.status.${tn.status}`)}
                        </span>
                      </td>
                      <td className="num">{tn.connectionCount}</td>
                      <td className="right">
                        <div className="row-actions">
                          <button
                            className="btn btn-sm"
                            type="button"
                            data-od-id={`tunnel-setup-${tn.id}`}
                            onClick={() => setDangXem(tn)}
                          >
                            {t('tunnel.setup')}
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            type="button"
                            disabled={!canWrite || xoa.isPending}
                            onClick={() => xoa.mutate(tn.id)}
                          >
                            {t('common.delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </AsyncSection>

      {dangXem ? (
        <TunnelSetupPanel
          connectionId={connectionId}
          tunnel={dangXem}
          canWrite={canWrite}
          onClose={() => setDangXem(null)}
        />
      ) : null}
    </Modal>
  );
}

/**
 * Bang huong dan cai dat + cau hinh ingress cho MOT tunnel.
 */
function TunnelSetupPanel({
  connectionId,
  tunnel,
  canWrite,
  onClose,
}: {
  connectionId: string;
  tunnel: Tunnel;
  canWrite: boolean;
  onClose: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const [token, setToken] = useState<TunnelToken | null>(null);
  const [ingress, setIngress] = useState<IngressRow[] | null>(null);
  const [xemNangCao, setXemNangCao] = useState<IngressRow | null>(null);
  const { error, setError, xoaLoiKhiGo } = useLoiApi();

  const cauHinh = useQuery({
    queryKey: ['tunnel-config', connectionId, tunnel.id],
    queryFn: () =>
      api.get<TunnelConfig>(`/connections/${connectionId}/tunnels/${tunnel.id}/config`),
    retry: false,
  });

  const layToken = useMutation({
    mutationFn: () =>
      api.post<TunnelToken>(`/connections/${connectionId}/tunnels/${tunnel.id}/token`, {}),
    onSuccess: (data) => setToken(data),
    onError: (err: unknown) => setError(thanhApiError(err)),
  });

  const luu = useMutation({
    mutationFn: (rows: IngressRow[]) =>
      api.put<{ ingress: IngressRow[] }>(
        `/connections/${connectionId}/tunnels/${tunnel.id}/config`,
        { ingress: rows.map((r) => ({ hostname: r.hostname ?? '', service: r.service })) },
      ),
    onSuccess: async () => {
      setError(null);
      await qc.invalidateQueries({ queryKey: ['tunnel-config', connectionId, tunnel.id] });
      toast.toast(t('tunnel.configSaved'));
    },
    onError: (err: unknown) => setError(thanhApiError(err)),
  });

  /*
   * Chi hien cac muc CO hostname de sua.
   *
   * Muc cuoi cua Cloudflare la catch-all khong hostname (`http_status:404`) —
   * mot chi tiet ky thuat bat buoc, khong phai lua chon cua nguoi dung. May chu
   * tu them lai khi luu, nen dua no ra man hinh chi tao co hoi xoa nham.
   */
  const dong = ingress ?? (cauHinh.data?.ingress ?? []).filter((r) => r.hostname);

  return (
    <Modal
      open
      title={t('tunnel.setupTitle', { name: tunnel.name })}
      onClose={onClose}
      onFieldChange={xoaLoiKhiGo.onChange}
      footer={
        <button className="btn" type="button" onClick={onClose}>
          {t('common.close')}
        </button>
      }
    >
      {error ? <ApiErrorAlert error={error} /> : null}

      {tunnel.status === 'inactive' ? (
        <div className="scope-note warn" data-od-id="tunnel-inactive-note">
          <Icon name="warn" size={17} />
          <span>{t('tunnel.inactiveNote')}</span>
        </div>
      ) : null}

      <h4>{t('tunnel.step1')}</h4>
      <p className="muted">{t('tunnel.step1Hint')}</p>

      {token ? (
        <>
          <div className="scope-note warn">
            <Icon name="warn" size={17} />
            <span>{t('tunnel.tokenWarn')}</span>
          </div>
          <pre className="code-block" data-od-id="tunnel-install-cmd">
            {token.installCommand}
          </pre>
          <p className="muted">{t('tunnel.dockerAlt')}</p>
          <pre className="code-block">{token.dockerCommand}</pre>
        </>
      ) : (
        <button
          className="btn"
          type="button"
          data-od-id="tunnel-token-btn"
          disabled={layToken.isPending}
          onClick={() => layToken.mutate()}
        >
          {layToken.isPending ? t('common.processing') : t('tunnel.showToken')}
        </button>
      )}

      <h4>{t('tunnel.step2')}</h4>
      <p className="muted">{t('tunnel.step2Hint', { target: tunnel.cnameTarget })}</p>

      <h4>{t('tunnel.step3')}</h4>
      {cauHinh.data && !cauHinh.data.editable ? (
        <div className="scope-note warn">
          <Icon name="info" size={17} />
          <span>{t('tunnel.localHint')}</span>
        </div>
      ) : (
        <>
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('tunnel.colHostname')}</th>
                  <th>{t('tunnel.colService')}</th>
                  <th className="right" />
                </tr>
              </thead>
              <tbody>
                {dong.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <input
                        value={r.hostname ?? ''}
                        placeholder="app.vidu.vn"
                        onChange={(e) => {
                          const ke = [...dong];
                          ke[i] = { ...r, hostname: e.target.value };
                          setIngress(ke);
                        }}
                      />
                    </td>
                    <td>
                      <input
                        value={r.service}
                        placeholder="http://localhost:8080"
                        onChange={(e) => {
                          const ke = [...dong];
                          ke[i] = { ...r, service: e.target.value };
                          setIngress(ke);
                        }}
                      />
                    </td>
                    <td className="right">
                      {demNangCao(r.advanced) > 0 ? (
                        /*
                         * DẤU HIỆU cho cấu hình màn hình này KHÔNG sửa được.
                         *
                         * Bốn dòng trông đơn giản làm người đọc tưởng đã thấy
                         * hết. Một hostname đang được Cloudflare Access bảo vệ
                         * mà không có dấu gì thì: xoá dòng rồi thêm lại là mất
                         * lớp đăng nhập — và lần ấy chính người dùng xoá, nên
                         * phép hợp nhất của OQ-339 không cứu được.
                         */
                        <button
                          className="btn btn-sm btn-ghost chip-nangcao"
                          type="button"
                          data-od-id={`tunnel-advanced-${i}`}
                          title={t('tunnel.advancedHint')}
                          onClick={() => setXemNangCao(r)}
                        >
                          <Icon name="settings" size={13} />
                          {t('tunnel.advancedCount', { count: String(demNangCao(r.advanced)) })}
                        </button>
                      ) : null}
                      <button
                        className="btn btn-sm btn-ghost"
                        type="button"
                        onClick={() => setIngress(dong.filter((_, j) => j !== i))}
                      >
                        {t('common.delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="row-actions">
            <button
              className="btn btn-sm"
              type="button"
              data-od-id="tunnel-ingress-add"
              onClick={() => setIngress([...dong, { hostname: '', service: '', path: null }])}
            >
              {t('tunnel.addRule')}
            </button>
            <button
              className="btn btn-primary btn-sm"
              type="button"
              data-od-id="tunnel-ingress-save"
              disabled={!canWrite || luu.isPending}
              onClick={() => luu.mutate(dong)}
            >
              {luu.isPending ? t('common.processing') : t('common.save')}
            </button>
          </div>

          {demNangCao(cauHinh.data?.advancedAll) > 0 ? (
            <div className="scope-note" data-od-id="tunnel-advanced-all">
              <Icon name="info" size={17} />
              <span>
                {t('tunnel.advancedAll', {
                  count: String(demNangCao(cauHinh.data?.advancedAll)),
                })}
              </span>
            </div>
          ) : null}
        </>
      )}

      {xemNangCao ? (
        <Modal
          open
          title={t('tunnel.advancedTitle')}
          subtitle={xemNangCao.hostname ?? ''}
          onClose={() => setXemNangCao(null)}
          footer={
            <button className="btn" type="button" onClick={() => setXemNangCao(null)}>
              {t('common.close')}
            </button>
          }
        >
          {/*
            CHỈ ĐỌC — có chủ ý.
            16 tuỳ chọn là quá nhiều để làm cho tử tế trong màn này, và làm nửa
            vời còn nguy hiểm hơn không làm: một ô `access` sửa sai là gỡ mất
            lớp đăng nhập. Ở đây chỉ nói cho người dùng biết có gì và sửa ở đâu.
          */}
          <div className="scope-note">
            <Icon name="info" size={17} />
            <span>{t('tunnel.advancedReadOnly')}</span>
          </div>
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('tunnel.advancedCol')}</th>
                  <th>{t('tunnel.advancedVal')}</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(xemNangCao.advanced ?? {}).map(([k, v]) => (
                  <tr key={k}>
                    <td className="mono">{k}</td>
                    <td className="tunnel-ten">
                      {hienGiaTri(v, { bat: t('common.on'), tat: t('common.off') })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      ) : null}
    </Modal>
  );
}
